import { Provider } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, map, of } from 'rxjs';
import {
  BREADCRUMB_LABEL_RESOLVERS,
  BREADCRUMB_SIBLINGS_LOADERS,
  BreadcrumbLabelResolver,
  BreadcrumbSiblingsLoader,
  BreadcrumbItem,
  BreadcrumbResolvedItem,
  fetchAllPages,
  isNavSection,
} from '@zag/ui';
import { PermissionService } from '@zag/auth/ui';
import { NAV_CONFIG } from './navigation.config';
import { type Calendar, type MyCalendarsResponse } from '@zag/calendar/models';

/**
 * API response types for breadcrumb data fetching.
 */
interface PersonListItem {
  id: string;
  firstName: string;
  lastName: string;
}

interface NamedListItem {
  id: string;
  name: string;
}

interface AppearanceItem extends NamedListItem {
  icon?: string | null;
  color?: string | null;
  imageUrl?: string | null;
}

/**
 * Helper to extract data from API response.
 * Some endpoints return {data: T}, others return T directly.
 *
 * We detect a wrapper by checking if 'data' is present AND the object
 * has only wrapper-like keys (data, meta, pagination, etc.)
 */
function extractData<T>(response: T | { data: T } | undefined): T | undefined {
  if (!response) return undefined;
  if (typeof response === 'object' && 'data' in response) {
    const keys = Object.keys(response);
    // If the only keys are wrapper keys, extract .data
    const wrapperKeys = new Set(['data', 'meta', 'pagination', 'total', 'page', 'limit']);
    const isWrapper = keys.every(key => wrapperKeys.has(key));
    if (isWrapper) {
      return (response as { data: T }).data;
    }
  }
  return response as T;
}

/**
 * Factory for creating label resolvers.
 * These resolve dynamic labels (e.g., person ID -> person name).
 */
function createLabelResolvers(http: HttpClient): Record<string, BreadcrumbLabelResolver> {
  return {
    person: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(http.get<PersonListItem | { data: PersonListItem }>(`/api/v1/core/persons/${params['id']}`));
        const person = extractData(response);
        return person ? `${person.firstName} ${person.lastName}` : params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    team: async (params: Record<string, string>): Promise<BreadcrumbResolvedItem | string> => {
      try {
        const response = await firstValueFrom(
          http.get<AppearanceItem | { data: AppearanceItem }>(`/api/v1/planning/teams/${params['id']}`)
        );
        const team = extractData(response);
        if (!team || !team.name) return params['id'] || 'Unknown';
        return { label: team.name, icon: team.icon ?? null, color: team.color ?? null, imageUrl: team.imageUrl ?? null };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    skill: async (params: Record<string, string>): Promise<BreadcrumbResolvedItem | string> => {
      try {
        const response = await firstValueFrom(http.get<AppearanceItem | { data: AppearanceItem }>(`/api/v1/core/skills/${params['id']}`));
        const skill = extractData(response);
        if (!skill || !skill.name) return params['id'] || 'Unknown';
        return { label: skill.name, icon: skill.icon ?? null, color: skill.color ?? null, imageUrl: skill.imageUrl ?? null };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    shift: async (params: Record<string, string>): Promise<BreadcrumbResolvedItem | string> => {
      try {
        const response = await firstValueFrom(
          http.get<AppearanceItem | { data: AppearanceItem }>(`/api/v1/planning/shifts/${params['id']}`)
        );
        const shift = extractData(response);
        if (!shift || !shift.name) return params['id'] || 'Unknown';
        return { label: shift.name, icon: shift.icon ?? null, color: shift.color ?? null, imageUrl: null };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    plan: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(http.get<NamedListItem | { data: NamedListItem }>(`/api/v1/planning/plans/${params['id']}`));
        const plan = extractData(response);
        return plan?.name || params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    strategy: async (params: Record<string, string>): Promise<BreadcrumbResolvedItem | string> => {
      try {
        const response = await firstValueFrom(
          http.get<AppearanceItem | { data: AppearanceItem }>(`/api/v1/planning/strategies/${params['id']}`)
        );
        const strategy = extractData(response);
        if (!strategy || !strategy.name) return params['id'] || 'Unknown';
        return { label: strategy.name, icon: strategy.icon ?? null, color: strategy.color ?? null, imageUrl: null };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    shiftUnit: async (params: Record<string, string>) => {
      try {
        const unitId = params['unitId'] || params['id'];
        if (!unitId) return 'Unknown';
        const response = await firstValueFrom(
          http.get<{ shift: NamedListItem } | { data: { shift: NamedListItem } }>(`/api/v1/planning/shift-units/${unitId}`)
        );
        const unit = extractData(response);
        return unit?.shift?.name || unitId;
      } catch {
        return params['unitId'] || params['id'] || 'Unknown';
      }
    },

    role: async (params: Record<string, string>) => {
      // Roles use their ID as the display name (e.g., "admin", "manager")
      return params['id'] || 'Unknown';
    },

    group: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(http.get<NamedListItem | { data: NamedListItem }>(`/api/v1/rbac/groups/${params['id']}`));
        const group = extractData(response);
        return group?.name || params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    user: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<{ name: string } | { data: { name: string } }>(`/api/v1/rbac/users/${params['id']}`)
        );
        const user = extractData(response);
        return user?.name || params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    location: async (params: Record<string, string>): Promise<BreadcrumbResolvedItem | string> => {
      try {
        const response = await firstValueFrom(
          http.get<AppearanceItem | { data: AppearanceItem }>(`/api/v1/core/locations/${params['id']}`)
        );
        const location = extractData(response);
        if (!location || !location.name) return params['id'] || 'Unknown';
        return { label: location.name, icon: location.icon ?? null, color: location.color ?? null, imageUrl: location.imageUrl ?? null };
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    notification: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<{ id: string; title: string } | { data: { id: string; title: string } }>(`/api/v1/notifications/${params['id']}`)
        );
        const notification = extractData(response);
        return notification?.title || params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    webhook: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<NamedListItem | { data: NamedListItem }>(`/api/v1/notifications/webhooks/${params['id']}`)
        );
        const webhook = extractData(response);
        return webhook?.name || params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    calendar: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<NamedListItem | { data: NamedListItem }>(`/api/v1/calendar/calendars/${params['id']}`)
        );
        const calendar = extractData(response);
        return calendar?.name || params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    customPropertyDefinition: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<{ label: string } | { data: { label: string } }>(`/api/v1/core/custom-property-definitions/${params['id']}`)
        );
        const def = extractData(response);
        return def?.label || params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    slotTypeDefinition: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<{ label: string } | { data: { label: string } }>(`/api/v1/calendar/slot-type-definitions/${params['id']}`)
        );
        const def = extractData(response);
        return def?.label || params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    balanceTypeDefinition: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<{ label: string } | { data: { label: string } }>(`/api/v1/time-tracking/balance-type-definitions/${params['id']}`)
        );
        const def = extractData(response);
        return def?.label || params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },

    documentType: async (params: Record<string, string>) => {
      try {
        const response = await firstValueFrom(
          http.get<{ label: string } | { data: { label: string } }>(`/api/v1/documents/document-types/${params['id']}`)
        );
        const def = extractData(response);
        return def?.label || params['id'] || 'Unknown';
      } catch {
        return params['id'] || 'Unknown';
      }
    },
  };
}

function toSiblingItems<T extends { id: string }>(items: T[], currentUrl: string, mapItem: (item: T) => BreadcrumbItem): BreadcrumbItem[] {
  return items.filter(item => !currentUrl.includes(item.id)).map(mapItem);
}

/**
 * Factory for creating siblings loaders.
 * These load sibling items for dropdown navigation.
 */
function createSiblingsLoaders(http: HttpClient, permissionService: PermissionService): Record<string, BreadcrumbSiblingsLoader> {
  return {
    // Entity-based siblings (loaded from API)
    persons: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<PersonListItem>(http, { basePath: '/api/v1/core/persons', sort: 'lastName:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, person => ({
            label: `${person.firstName} ${person.lastName}`,
            route: ['/persons', person.id, 'edit'],
          }))
        )
      ),

    teams: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<AppearanceItem>(http, { basePath: '/api/v1/planning/teams', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, team => ({
            label: team.name,
            route: ['/teams', team.id],
            icon: team.icon ?? 'groups',
            color: team.color ?? null,
            imageUrl: team.imageUrl ?? null,
          }))
        )
      ),

    skills: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<AppearanceItem>(http, { basePath: '/api/v1/core/skills', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, skill => ({
            label: skill.name,
            route: ['/skills-catalog', skill.id],
            icon: skill.icon ?? 'school',
            color: skill.color ?? null,
            imageUrl: skill.imageUrl ?? null,
          }))
        )
      ),

    shifts: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<AppearanceItem>(http, { basePath: '/api/v1/planning/shifts', params: { type: 'definition' }, sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, shift => ({
            label: shift.name,
            route: ['/shift-definitions', shift.id],
            icon: shift.icon ?? 'schedule',
            color: shift.color ?? null,
          }))
        )
      ),

    plans: (_params: Record<string, string>, currentUrl: string) => {
      // Detect if we're on an edit page to preserve the edit suffix
      const isEditPage = currentUrl.endsWith('/edit');
      return fetchAllPages<NamedListItem>(http, { basePath: '/api/v1/planning/plans', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, plan => ({
            label: plan.name,
            route: isEditPage ? ['/planning', 'plans', plan.id, 'edit'] : ['/planning', 'plans', plan.id],
          }))
        )
      );
    },

    strategies: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<AppearanceItem>(http, { basePath: '/api/v1/planning/strategies', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, strategy => ({
            label: strategy.name,
            route: ['/planning', 'strategies', strategy.id],
            icon: strategy.icon ?? 'lightbulb',
            color: strategy.color ?? null,
          }))
        )
      ),

    roles: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<{ id: string; description?: string }>(http, { basePath: '/api/v1/rbac/roles' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, role => ({
            label: role.id,
            route: ['/settings', 'roles', role.id],
          }))
        )
      ),

    groups: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<NamedListItem>(http, { basePath: '/api/v1/rbac/groups', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, group => ({
            label: group.name,
            route: ['/settings', 'groups', group.id],
          }))
        )
      ),

    users: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<{ id: string; name: string }>(http, { basePath: '/api/v1/rbac/users', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, user => ({
            label: user.name,
            route: ['/settings', 'users', user.id],
          }))
        )
      ),

    locations: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<AppearanceItem>(http, { basePath: '/api/v1/core/locations', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, location => ({
            label: location.name,
            route: ['/locations', location.id],
            icon: location.icon ?? 'location_on',
            color: location.color ?? null,
            imageUrl: location.imageUrl ?? null,
          }))
        )
      ),

    notifications: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<{ id: string; title: string }>(http, { basePath: '/api/v1/notifications', sort: 'createdAt:desc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, notification => ({
            label: notification.title,
            route: ['/notifications', notification.id],
          }))
        )
      ),

    webhooks: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<NamedListItem>(http, { basePath: '/api/v1/notifications/webhooks', sort: 'name:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, webhook => ({
            label: webhook.name,
            route: ['/settings', 'notifications', 'webhooks', webhook.id],
          }))
        )
      ),

    calendars: (_params: Record<string, string>, currentUrl: string) =>
      http.get<MyCalendarsResponse>(`/api/v1/users/me/calendars`).pipe(
        map(response => {
          const mine = response;
          const ownedIds = new Set(mine.personalCalendarIds);
          const readOnlyOwnedIds = new Set(mine.readOnlyPersonalCalendarIds ?? []);

          // Extract ID from currentUrl to filter out the current calendar
          // URL pattern: .../calendars-list/UUID
          const match = currentUrl.match(/\/calendars-list\/([0-9a-f-]{36})/);
          const currentId = match ? match[1] : null;

          // Only include calendars the user can actually edit in the settings page.
          // Global calendars are always read-only; exclude them from the list.
          const editablePersonal = mine.individual.filter(
            c => c.id !== currentId && (mine.canEditAll || (ownedIds.has(c.id) && !readOnlyOwnedIds.has(c.id)))
          );
          const editableLocation = mine.factory.filter((c: Calendar) => c.id !== currentId && mine.canEditAll);

          const items: BreadcrumbItem[] = [];

          if (editablePersonal.length > 0) {
            items.push({ separator: true, sectionLabel: 'calendars.type.individual' });
            editablePersonal.slice(0, 8).forEach((c: Calendar) => items.push({ label: c.name, route: ['/calendars-list', c.id] }));
          }

          if (editableLocation.length > 0) {
            items.push({ separator: true, sectionLabel: 'calendars.type.factory' });
            editableLocation.slice(0, 8).forEach(c => items.push({ label: c.name, route: ['/calendars-list', c.id] }));
          }

          return items;
        })
      ),

    customPropertyDefinitions: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<{ id: string; label: string }>(http, { basePath: '/api/v1/core/custom-property-definitions', sort: 'label:asc' }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, definition => ({
            label: definition.label,
            route: ['/settings', 'custom-properties', definition.id],
          }))
        )
      ),

    absenceTypes: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<{ id: string; label: string }>(http, {
        basePath: '/api/v1/calendar/slot-type-definitions',
        params: { category: 'absence' },
        sort: 'label:asc',
      }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, absenceType => ({
            label: absenceType.label,
            route: ['/settings', 'absences', absenceType.id],
          }))
        )
      ),

    documentTypes: (_params: Record<string, string>, currentUrl: string) =>
      http.get<{ id: string; label: string }[]>('/api/v1/documents/document-types').pipe(
        map(items =>
          toSiblingItems(items, currentUrl, documentType => ({
            label: documentType.label,
            route: ['/settings', 'document-types', documentType.id],
          }))
        )
      ),

    balanceTypes: (_params: Record<string, string>, currentUrl: string) =>
      fetchAllPages<{ id: string; label: string }>(http, {
        basePath: '/api/v1/time-tracking/balance-type-definitions',
        sort: 'label:asc',
      }).pipe(
        map(items =>
          toSiblingItems(items, currentUrl, balanceType => ({
            label: balanceType.label,
            route: ['/settings', 'balances', balanceType.id],
          }))
        )
      ),

    // Settings pages (static list — all settings sub-routes for breadcrumb dropdown navigation).
    // Unlike entity siblings (API-loaded), these are navigation-based (same pattern as planningChildren).
    // Each page is permission-gated: only pages the current user can access appear in the dropdown.
    settingsPages: (_params: Record<string, string>, currentUrl: string) => {
      const pages: { label: string; route: string[]; segment: string; permission?: string }[] = [
        { label: 'navigation.users', route: ['/settings', 'users'], segment: 'users', permission: 'settings:update' },
        { label: 'navigation.groups', route: ['/settings', 'groups'], segment: 'groups', permission: 'settings:update' },
        { label: 'navigation.roles', route: ['/settings', 'roles'], segment: 'roles', permission: 'settings:update' },
        { label: 'navigation.permissions', route: ['/settings', 'permissions'], segment: 'permissions', permission: 'settings:update' },
        {
          label: 'settings.auth.title',
          route: ['/settings', 'authentication'],
          segment: 'authentication',
          permission: 'settings:auth:read',
        },
        { label: 'settings.smtp.title', route: ['/settings', 'email'], segment: 'email', permission: 'settings:smtp:read' },
        { label: 'settings.ai.title', route: ['/settings', 'ai'], segment: 'ai', permission: 'settings:ai:read' },
        {
          label: 'settings.directory.title',
          route: ['/settings', 'directory'],
          segment: 'directory',
          permission: 'settings:directory:read',
        },
        {
          label: 'notifications.channelConfigs.title',
          route: ['/settings', 'notifications'],
          segment: 'notifications',
          permission: 'notifications:admin',
        },
        { label: 'settings.nav.apiKeys', route: ['/settings', 'api-keys'], segment: 'api-keys', permission: 'users:profile:read' },
        { label: 'navigation.webhooks', route: ['/settings', 'webhooks'], segment: 'webhooks', permission: 'webhooks:read' },
        {
          label: 'settings.customPropertyDefinitions.title',
          route: ['/settings', 'custom-properties'],
          segment: 'custom-properties',
          permission: 'settings:read',
        },
        {
          label: 'files.admin.title',
          route: ['/settings', 'files'],
          segment: 'files',
          permission: 'files:read',
        },
        {
          label: 'documents.documentTypes.title',
          route: ['/settings', 'document-types'],
          segment: 'document-types',
          permission: 'documents:configure',
        },
        { label: 'navigation.ruleCatalog', route: ['/settings', 'rules'], segment: 'rules', permission: 'settings:read' },
        { label: 'navigation.absenceTypes', route: ['/settings', 'absences'], segment: 'absences', permission: 'settings:read' },
        { label: 'navigation.balanceAccounts', route: ['/settings', 'balances'], segment: 'balances', permission: 'settings:read' },
        { label: 'settings.invoicing.title', route: ['/settings', 'invoicing'], segment: 'invoicing', permission: 'projects:read' },
      ];

      // Extract the settings sub-segment from currentUrl (e.g. '/settings/email' → 'email')
      const urlSegments = currentUrl.replace(/^\//, '').split('/');
      const currentSegment = urlSegments[1] ?? '';

      const siblings: BreadcrumbItem[] = pages
        .filter(p => p.segment !== currentSegment)
        .filter(p => !p.permission || permissionService.hasPermission(p.permission))
        .map(p => ({ label: p.label, route: p.route }));

      return of(siblings);
    },

    // Navigation-based siblings (derived from NAV_CONFIG — Planning section items)
    planningChildren: (_params: Record<string, string>, currentUrl: string) => {
      // Find the Planning section in the nav config
      const planningSection = NAV_CONFIG.find(entry => isNavSection(entry) && entry.label === 'navigation.sectionPlanning');
      if (!planningSection || !isNavSection(planningSection)) {
        return of([]);
      }

      const siblings: BreadcrumbItem[] = planningSection.items
        .filter((item): item is typeof item & { route: string } => typeof item.route === 'string' && item.route !== currentUrl)
        .map(item => ({
          label: item.label,
          icon: item.icon,
          route: ['/', ...item.route.split('/').filter(Boolean)],
        }));

      return of(siblings);
    },
  };
}

/**
 * Provides breadcrumb resolvers and loaders for the application.
 * Add this to your app.config.ts providers array.
 */
export function provideBreadcrumbConfig(): Provider[] {
  return [
    {
      provide: BREADCRUMB_LABEL_RESOLVERS,
      useFactory: createLabelResolvers,
      deps: [HttpClient],
    },
    {
      provide: BREADCRUMB_SIBLINGS_LOADERS,
      useFactory: createSiblingsLoaders,
      deps: [HttpClient, PermissionService],
    },
  ];
}
